// SM-2 lite scheduler for spaced retrieval.
//
// Pure functions. No I/O, no clock — `nowMs` always passed in. Lets tests
// run deterministic timelines and lets the same code execute server-side
// (RFC-0006 cutover) with no behavior change.
//
// Per product/design/student-personalization.md build queue step 4. The
// "lite" caveat: classic SM-2 takes grade ∈ {0..5} and adjusts ease via the
// quality-of-recall formula; we collapse to a four-button Anki-style grade
// {1..4} = {Again, Hard, Good, Easy}. Trading a little fidelity for a
// vastly simpler student-facing surface. Easy to swap to FSRS later — the
// RecallState shape is the contract, the arithmetic isn't.

import type { FlashcardGrade, RecallState } from '@disha/contracts';

const DEFAULT_EASE = 2.5;
const MIN_EASE = 1.3;
const MAX_EASE = 3.0;
// First successful grade ("Good" on a new card) → 1 day.
// Second consecutive successful grade → 6 days. Classic SM-2 seed.
const FIRST_INTERVAL_DAYS = 1;
const SECOND_INTERVAL_DAYS = 6;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function clampEase(ease: number): number {
  return Math.max(MIN_EASE, Math.min(MAX_EASE, ease));
}

function isoFromMs(ms: number): string {
  return new Date(ms).toISOString();
}

/**
 * Fresh state for an artifact the student has never reviewed.
 * `dueAt` defaults to `nowMs` so new cards appear in the due set immediately
 * — the loop is "see it once in /notes" → grade it → schedule kicks in.
 */
export function newRecallState(artifactId: string, nowMs: number): RecallState {
  return {
    artifactId,
    intervalDays: 0,
    easeFactor: DEFAULT_EASE,
    dueAt: isoFromMs(nowMs),
    lastGradedAt: null,
    reps: 0,
    lapseCount: 0,
  };
}

/**
 * Apply a grade. Pure: returns a new RecallState.
 *
 * Grade semantics (Anki-style):
 *   1 Again — reset interval; bump lapseCount; ease −0.2
 *   2 Hard  — keep / barely-bump interval; ease −0.15
 *   3 Good  — interval *= ease; ease unchanged
 *   4 Easy  — interval *= ease * 1.3; ease +0.10
 *
 * After the first successful grade, interval seeds to FIRST_INTERVAL_DAYS.
 * After the second consecutive successful grade, interval jumps to
 * SECOND_INTERVAL_DAYS. From then on, interval *= ease per "Good".
 */
export function scheduleRecall(
  state: RecallState,
  grade: FlashcardGrade,
  nowMs: number,
): RecallState {
  const reps = state.reps + 1;
  const lastGradedAt = isoFromMs(nowMs);

  if (grade === 1) {
    // Lapse: card resets, ease shrinks, item due again today.
    return {
      ...state,
      intervalDays: 0,
      easeFactor: clampEase(state.easeFactor - 0.2),
      dueAt: isoFromMs(nowMs),
      lastGradedAt,
      reps,
      lapseCount: state.lapseCount + 1,
    };
  }

  // Compute next interval based on previous successful interval + grade.
  let nextInterval: number;
  const ease = clampEase(state.easeFactor + (grade === 2 ? -0.15 : grade === 4 ? 0.1 : 0));

  if (state.intervalDays === 0) {
    // First successful rep.
    nextInterval = FIRST_INTERVAL_DAYS;
  } else if (state.intervalDays <= FIRST_INTERVAL_DAYS) {
    // Second successful rep — jump to a week-ish.
    nextInterval = SECOND_INTERVAL_DAYS;
  } else {
    // Steady state: multiply by ease. Hard caps the multiplier so the
    // interval doesn't shrink on Hard.
    const multiplier = grade === 4 ? ease * 1.3 : grade === 2 ? Math.max(1.2, ease - 0.5) : ease;
    nextInterval = state.intervalDays * multiplier;
  }

  // Round down to whole days, cap at 365.
  nextInterval = Math.min(365, Math.max(1, Math.floor(nextInterval)));
  const dueAt = isoFromMs(nowMs + nextInterval * MS_PER_DAY);

  return {
    ...state,
    intervalDays: nextInterval,
    easeFactor: ease,
    dueAt,
    lastGradedAt,
    reps,
    lapseCount: state.lapseCount,
  };
}

/**
 * Pick artifact IDs whose `dueAt` is at or before `nowMs`. Sorted by `dueAt`
 * ascending so most-overdue cards surface first — "spaced", not "most recent".
 */
export function dueArtifactIds(
  states: Readonly<Record<string, RecallState>>,
  nowMs: number,
): string[] {
  const due: Array<{ id: string; dueMs: number }> = [];
  for (const [id, s] of Object.entries(states)) {
    const dueMs = Date.parse(s.dueAt);
    if (Number.isNaN(dueMs)) continue;
    if (dueMs <= nowMs) due.push({ id, dueMs });
  }
  due.sort((a, b) => a.dueMs - b.dueMs);
  return due.map((d) => d.id);
}
